"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-accent-teal text-white hover:bg-accent-teal/90 hover:shadow-lg hover:shadow-accent-teal/30",
        red: "bg-accent-red text-white hover:bg-accent-red/90 hover:shadow-lg hover:shadow-accent-red/30",
        outline:
          "border-2 border-accent-teal text-accent-teal hover:bg-accent-teal hover:text-white",
        ghost:
          "hover:bg-accent-teal/10 hover:text-accent-teal dark:hover:bg-accent-teal/20",
        link: "text-accent-teal underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    // Separate out any conflicting props
    const { onAnimationStart, ...restProps } = props
    
    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
        {...(restProps as any)}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

